import { Copy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type { Child, SiteRule } from '../lib/types';
import { Avatar } from '../ui/Avatar';
import { Sheet } from '../ui/Sheet';
import { useParent } from './ParentContext';
import { canDo } from './Plans';

type Group = 'sites' | 'apps' | 'schedule' | 'features' | 'monitoring' | 'subjects';
type How = 'add' | 'replace';
type SingleApp = NonNullable<Child['blockedApps']>[number];

/** The other children in this family whose settings this parent can change (#765). */
function useSiblings(child: Child) {
  const { children } = useParent();
  return useMemo(
    () => children.filter((c) => c.id !== child.id && (c.familyId ?? '') === (child.familyId ?? '') && canDo(c, 'child_settings', 'edit')),
    [children, child.id, child.familyId],
  );
}

/** Ticks for "who", as avatar pills. */
function ChildPicks({ kids, ids, onChange }: { kids: Child[]; ids: string[]; onChange: (ids: string[]) => void }) {
  return (
    <span className="copy-kids" role="group" aria-label="Children">
      {kids.map((k) => {
        const on = ids.includes(k.id);
        return (
          <button
            key={k.id}
            type="button"
            className={`copy-kid ${on ? 'on' : ''}`}
            aria-pressed={on}
            onClick={() => onChange(on ? ids.filter((i) => i !== k.id) : [...ids, k.id])}
          >
            <Avatar name={k.name} color={k.color} size={22} /> {k.name}
          </button>
        );
      })}
    </span>
  );
}

/**
 * "Also for" (#765): when a parent blocks or approves a site or app for one child, the same is added for the ticked
 * siblings. It starts with everyone ticked or no one, as the admin sets. Only ever adds; removing stays per child.
 */
export function useAlsoFor(child: Child) {
  const { parent, replaceChild, toast } = useParent();
  const siblings = useSiblings(child);
  const [ids, setIds] = useState<string[]>(() => (parent.alsoForAll === false ? [] : siblings.map((s) => s.id)));
  useEffect(() => setIds(parent.alsoForAll === false ? [] : siblings.map((s) => s.id)), [child.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const also = useCallback(
    async (change: { site?: { kind: SiteRule['kind']; pattern: string }; blockApp?: SingleApp; approveApp?: SingleApp; blockCategory?: string }) => {
      if (!ids.length) return;
      try {
        const r = await api.post<{ children: Child[] }>(`/api/children/${child.id}/also`, { to: ids, ...change });
        r.children.forEach(replaceChild);
      } catch (e) {
        toast({ text: (e as Error).message, tone: 'alert' });
      }
    },
    [child.id, ids, replaceChild, toast],
  );

  const picker = siblings.length ? (
    <div className="also-for">
      <span className="muted small">New sites and apps also for:</span>
      <ChildPicks kids={siblings} ids={ids} onChange={setIds} />
    </div>
  ) : null;
  return { also, picker };
}

const GROUPS: { id: Group; label: string; lists: boolean; summary: (c: Child, rules: SiteRule[] | null) => string }[] = [
  {
    id: 'sites',
    label: 'Websites',
    lists: true,
    summary: (c, rules) =>
      rules
        ? `${rules.filter((r) => r.kind === 'block').length} blocked, ${rules.filter((r) => r.kind === 'learn').length} learning sites · Learning sites only: ${c.learningOnly ? 'on' : 'off'}`
        : '…',
  },
  {
    id: 'apps',
    label: 'Apps',
    lists: true,
    summary: (c) =>
      [
        c.blockCategories?.length ? `Blocked: ${c.blockCategories.map((x) => x[0]!.toUpperCase() + x.slice(1)).join(', ')}` : 'No categories blocked',
        `${c.blockedApps?.length ?? 0} blocked apps`,
        `${c.learningApps?.length ?? 0} approved apps`,
      ].join(' · '),
  },
  { id: 'schedule', label: 'School hours', lists: false, summary: (c) => `Weekly hours, the yearly pause, and School on: ${c.mode === 'auto' ? 'Auto' : c.mode === 'on' ? 'On' : 'Off'}` },
  { id: 'features', label: 'What they can use', lists: false, summary: (c) => (c.hiddenFeatures?.length ? `${c.hiddenFeatures.length} features hidden` : 'Every feature shown') },
  {
    id: 'monitoring',
    label: 'Camera blur and turning in work',
    lists: false,
    summary: (c) => `Blur ${c.blurAllowed === false ? 'not allowed' : 'allowed'} · “What I learned” ${c.requireLearned ? 'required' : 'optional'}`,
  },
  { id: 'subjects', label: 'Subjects', lists: true, summary: (c) => (c.subjects?.length ? c.subjects.join(', ') : 'None yet') },
];

/** Copy settings from one child to others (#765): pick who, and which groups; lists add by default. */
function CopySettingsSheet({ child, onClose }: { child: Child; onClose: () => void }) {
  const { children, replaceChild, toast } = useParent();
  const [fromId, setFromId] = useState(child.id);
  const from = children.find((c) => c.id === fromId) ?? child;
  const siblings = useSiblings(from);
  const sources = children.filter((c) => (c.familyId ?? '') === (child.familyId ?? '') && canDo(c, 'child_settings', 'view'));
  const [to, setTo] = useState<string[]>(() => siblings.map((s) => s.id));
  const [groups, setGroups] = useState<Partial<Record<Group, How>>>({ sites: 'add', apps: 'add', schedule: 'replace', features: 'replace', monitoring: 'replace' });
  const [rules, setRules] = useState<SiteRule[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRules(null);
    api.get<SiteRule[]>(`/api/children/${from.id}/rules`).then(setRules, () => setRules([]));
    setTo((ids) => ids.filter((i) => i !== from.id));
  }, [from.id]);

  const names = siblings.filter((s) => to.includes(s.id)).map((s) => s.name);
  async function copy() {
    setBusy(true);
    try {
      const r = await api.post<{ children: Child[] }>(`/api/children/${from.id}/copy-settings`, { to, groups });
      r.children.forEach(replaceChild);
      toast({ text: `Copied ${from.name}’s settings to ${names.join(' and ')}.`, tone: 'good' });
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Copy settings" onClose={onClose}>
      <p className="muted small">Make your children’s rules match without setting each one up again.</p>
      <label>
        Copy from
        <select value={from.id} onChange={(e) => setFromId(e.target.value)}>
          {sources.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <div className="copy-field">
        <span className="copy-label">To</span>
        {siblings.length ? <ChildPicks kids={siblings} ids={to} onChange={setTo} /> : <p className="muted small">No other children whose settings you can change.</p>}
      </div>
      <div className="copy-field">
        <span className="copy-label">What to copy</span>
        <div className="copy-groups">
          {GROUPS.map((g) => {
            const how = groups[g.id];
            return (
              <div key={g.id} className={`copy-group ${how ? '' : 'off'}`}>
                <label className="check">
                  <input type="checkbox" checked={!!how} onChange={(e) => setGroups((s) => ({ ...s, [g.id]: e.target.checked ? (g.lists ? 'add' : 'replace') : undefined }))} />
                  <span>
                    <strong>{g.label}</strong>
                    <span className="muted small block">{g.summary(from, rules)}</span>
                  </span>
                </label>
                {g.lists && how && (
                  <span className="seg" role="group" aria-label={`${g.label}: add or replace`}>
                    {(['add', 'replace'] as How[]).map((h) => (
                      <button key={h} type="button" className={how === h ? 'on' : ''} aria-pressed={how === h} onClick={() => setGroups((s) => ({ ...s, [g.id]: h }))}>
                        {h === 'add' ? 'Add' : 'Replace'}
                      </button>
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="muted small">
        <strong>Add</strong> keeps anything the other child already has; <strong>Replace</strong> makes it match exactly. Never copied: name, color,
        grade, Student Portal login, school and planner links, schoolwork and records.
      </p>
      <button className="btn primary big" disabled={busy || !to.length || !Object.values(groups).some(Boolean)} onClick={() => void copy()}>
        {names.length ? `Copy to ${names.join(' and ')}` : 'Pick who to copy to'}
      </button>
    </Sheet>
  );
}

/** "Copy settings…" on a child's page (#765), shown when there's another child to copy to. */
export function CopySettingsButton({ child }: { child: Child }) {
  const siblings = useSiblings(child);
  const [open, setOpen] = useState(false);
  if (!siblings.length) return null;
  return (
    <>
      <button className="btn small ghost" onClick={() => setOpen(true)}>
        <Copy size={15} /> Copy settings…
      </button>
      {open && <CopySettingsSheet child={child} onClose={() => setOpen(false)} />}
    </>
  );
}
